import { useAuth } from "context/AuthContext"
import { useSelectedTimings } from "context/TimingContext"
import { onlineTypeOptions } from "conts/options"
import {
  getNumberOfStudentsOptions,
  useTeacherSlots,
} from "hooks/useTeacherSlots"
import moment from "moment"
import { DiscountTooltip, PendingClasses } from "pages/user/components"
import { userRoute } from "pages/user/user.routes"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import styled from "styled-components"
import { ActionButton, ButtonPrimary } from "ui/base/Button/ButtonStyled"
import { DateCard } from "ui/base/dateCard/DateCard"
import { DatePicker } from "ui/base/datePicker/DatePicker"
import { LoaderComponent, PAGE_HEIGHT } from "ui/base/loading/LoaderComponent"
import { scrollbarStylesThin } from "ui/base/scrollbarStyle/scrollbarStyle"
import { SelectButton } from "ui/base/selectInput/SelectButton"
import { ToggleGroup } from "ui/base/toggle-group/toggle-group"
import { LoginModal } from "ui/blocks/AuthModals/loginModal/LoginModal"
import { SignupModal } from "ui/blocks/AuthModals/singupModal/SignupModal"
import { GraduationIcon, UsersIcon } from "ui/icons"
import { StarFullIcon } from "ui/icons/StarFullIcon"
import { UserIcon } from "ui/icons/UserIcon"
import { defaultThemeColors, fonts, mediaSizes } from "ui/theme"
import {
  formatMinutesToHours,
  getAvatarUrl,
  getClassType,
  getHighestDiscount,
  getLowestValidPrice,
} from "utils"
import { studentLevelMapping } from "utils/constants"
import { DiscountBadge } from "../../../../ui/base/discountBadge/DiscountBadge"
import { SendMessageModal } from "./SendMessageModal"

const onlineOptions = {
  online: onlineTypeOptions.filter((option) => option.value === "online"),
  offline: onlineTypeOptions.filter((option) => option.value === "offline"),
  both: onlineTypeOptions,
}

const typeMap = {
  true_true: "both",
  true_false: "offline",
  false_true: "online",
}

export const ClassInfo = ({ classDetail, pendingData }) => {
  const navigate = useNavigate()
  const { token } = useAuth()
  const [online_type, setOnline_type] = useState(null)
  const { state } = useLocation()
  const [showModal, setShowModal] = useState(false)
  const [showSignupModal, setShowSignupModal] = useState(false)

  useEffect(() => {
    if (state?.online_type) {
      setOnline_type(state?.online_type || defaultOnlineType)
    }
  }, [state])

  // TOOD: make one source of truth for numberOfStudents and typeOfClass
  const {
    typeOfClass: savedTypeOfClass,
    handleAddSelectedTiming,
    handleChangeNumberOfStudents,
    handleChangeTypeOfClass,
    clearStore,
  } = useSelectedTimings()

  const teacherInfo = classDetail?.teacher
  const onlineType = classDetail?.teacher.online_type

  const hasInPersonClasses = !!teacherInfo?.slots?.length
  const hasOnlineClasses = !!teacherInfo?.online_slots?.length
  const typeOfTeacherClass =
    typeMap[`${hasInPersonClasses}_${hasOnlineClasses}`]

  const defaultOnlineType = getClassType(onlineType, hasOnlineClasses)
  const isOnline = online_type === "online" && hasOnlineClasses

  const {
    selectedDate,
    setSelectedDate,
    typeOfClass,
    setTypeOfClass,
    numberOfStudents,
    setNumberOfStudents,
    availableTimings,
    slotsFilterDate,
    availableGroupTypes,
    isLoading,
    daysLoading,
    handleMonthChange,
  } = useTeacherSlots({
    teacherInfo,
    online_type: isOnline ? "online" : "offline",
  })

  const durationInHours = formatMinutesToHours(teacherInfo?.class_duration)
  const lowestPrice = isOnline
    ? teacherInfo?.price_per_online_class
    : getLowestValidPrice(
        teacherInfo?.type_of_class,
        teacherInfo?.price_per_group_class,
        teacherInfo?.price_per_private_class
      )

  const discountList = teacherInfo?.discount_policy_for_private_class || []
  const hasMultiple = discountList.length > 1
  const highestDiscount = getHighestDiscount(teacherInfo)
  const isFirstClassFree =
    highestDiscount?.policy === "First class" &&
    highestDiscount?.percentage === 100

  const renderAvailabilityInfo = (timing) => {
    if (timing.remainingSlots) {
      return (
        <CaptionSpot>
          {timing.remainingSlots}{" "}
          {timing.remainingSlots === 1 ? "spot" : "spots"} remaining
        </CaptionSpot>
      )
    }
    return null
  }

  const handleSelectDate = (timing) => {
    handleAddSelectedTiming(timing)
    navigate(userRoute.paymentPage + classDetail?.id)
  }

  const updateNumberOfStudents = (value) => {
    setNumberOfStudents(value)
    handleChangeNumberOfStudents(value)
  }

  useEffect(() => {
    clearStore()
  }, [])

  useEffect(() => {
    if (savedTypeOfClass?.value === typeOfClass?.value) return
    handleChangeTypeOfClass(typeOfClass)
  }, [typeOfClass])

  useEffect(() => {
    if (state?.online_type) {
      setOnline_type(state.online_type)
    } else {
      setOnline_type(defaultOnlineType)
    }
  }, [state, defaultOnlineType])

  useEffect(() => {
    if (online_type === "online" && typeOfTeacherClass === "offline") {
      setOnline_type("offline")
    } else if (online_type === "offline" && typeOfTeacherClass === "online") {
      setOnline_type("online")
    }
  }, [typeOfTeacherClass, online_type])

  const student_level = teacherInfo?.level_of_student.split(",")

  return (
    <ClassInfoLayout>
      {showSignupModal ? (
        <SignupModal
          show={showSignupModal}
          handleClose={() => setShowSignupModal(false)}
          setLoginModal={setShowModal}
        />
      ) : null}
      {showModal ? (
        <LoginModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          setSignupModal={setShowSignupModal}
          hasHelperText={false}
        />
      ) : null}
      <ClassDetailsSection>
        <ProfileInfoWrapper>
          <div className='profile-group'>
            <AvatarWrapper>
              {teacherInfo?.profile_pic ? (
                <img
                  src={getAvatarUrl(teacherInfo?.profile_pic)}
                  alt={classDetail?.first_name}
                />
              ) : (
                <UserIcon color='txt' />
              )}
            </AvatarWrapper>
            <ProfileInfo>
              <SectionHeading>{classDetail?.first_name}</SectionHeading>
              <RatingText>
                <p className='rating'>
                  <StarFullIcon />
                  {Number(teacherInfo?.rate || 0).toFixed(1)}
                </p>
                <p className='review'>({teacherInfo?.review || 0} reviews)</p>
              </RatingText>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "1rem",
                }}>
                <PricingDetails>
                  <span>from ${lowestPrice} </span>
                  <span>/ {durationInHours}</span>
                </PricingDetails>
                {highestDiscount && (
                  <DiscountBadge discount={highestDiscount} />
                )}
              </div>
            </ProfileInfo>
          </div>

          <div className='toggle-group'>
            <ToggleGroup
              size='small'
              options={onlineOptions[typeOfTeacherClass || defaultOnlineType]}
              value={online_type}
              onChange={(option) => {
                setOnline_type(option.value)
              }}
            />
          </div>
        </ProfileInfoWrapper>
        <ContentSection>
          <IconsWrapper>
            <IconTextWrapper>
              <UsersIcon /> <p>{teacherInfo?.type_of_class}</p>
            </IconTextWrapper>
            <IconTextWrapper>
              <GraduationIcon />
              <p>
                Teaching{" "}
                {student_level?.map((level, i) =>
                  level
                    ? studentLevelMapping[level] +
                      `${i < student_level.length - 1 ? " / " : " "}`
                    : " "
                )}{" "}
                students
              </p>
            </IconTextWrapper>
          </IconsWrapper>
          <p
            className='text'
            style={{
              textAlign: "justify",
            }}>
            {teacherInfo?.experience_on_subject}
          </p>
        </ContentSection>
        <ContentSection>
          <SectionHeading>What You Should Expect in My Class</SectionHeading>
          <p
            className='text'
            style={{
              textAlign: "justify",
            }}>
            {isOnline
              ? teacherInfo?.expected_students_online ||
                teacherInfo?.expected_students
              : teacherInfo?.expected_students}
          </p>
        </ContentSection>
        <ContentSection>
          <SectionHeading>What You Need to Have for My Class</SectionHeading>
          <p
            className='text'
            style={{
              textAlign: "justify",
            }}>
            {isOnline
              ? teacherInfo?.student_need_to_have_online ||
                teacherInfo?.student_need_to_have
              : teacherInfo?.student_need_to_have}
          </p>
        </ContentSection>
        <ContentSection style={{ width: "fit-content" }}>
          {classDetail ? (
            <SendMessageModal
              classDetail={classDetail}
              onSignupModal={setShowSignupModal}
            />
          ) : null}
        </ContentSection>
      </ClassDetailsSection>

      <PricingDetailsCard>
        {pendingData?.hasPending ? (
          <PendingClasses
            subjectToTeach={teacherInfo?.subject_want_to_teach}
            pendingMessage={pendingData.message}
          />
        ) : (
          <>
            <PricingHeaderWrapper>
              <HeaderCard>
                <SectionHeading>
                  {teacherInfo?.subject_sub_category}
                </SectionHeading>
                {highestDiscount && isFirstClassFree && (
                  <DiscountBadge discount={highestDiscount} />
                )}
              </HeaderCard>
              <PricingSubHeader>
                <PricingDetails>
                  <span>from ${lowestPrice} </span>
                  <span>/ {durationInHours}</span>
                </PricingDetails>
                {hasMultiple ? (
                  <DiscountTooltip
                    place='top-start'
                    discountList={discountList}
                  />
                ) : (
                  highestDiscount &&
                  !isFirstClassFree && (
                    <DiscountBadge discount={highestDiscount} />
                  )
                )}
              </PricingSubHeader>
            </PricingHeaderWrapper>

            <FilterControlsWrapper>
              <DatePicker
                minDate={moment().toDate()}
                maxDate={moment().add(2, "months")}
                date={selectedDate}
                setDate={setSelectedDate}
                filterDate={slotsFilterDate}
                dayClassName={(date) => {
                  if (daysLoading) return "loading-date"
                  return slotsFilterDate(date) ? "" : "unavailable-date"
                }}
                labelSize={"small"}
                onMonthChange={handleMonthChange}
              />
              <div className='mode-control'>
                <SelectButton
                  options={onlineOptions[typeOfTeacherClass || defaultOnlineType]}
                  value={onlineOptions[typeOfTeacherClass || defaultOnlineType].find(
                    (o) => o.value === online_type
                  )}
                  onChange={(option) => setOnline_type(option.value)}
                  bgColor='white'
                  showIndicator={typeOfTeacherClass === "both"}
                  isDisabled={typeOfTeacherClass !== "both"}
                  labelSize={"small"}
                  valueColor='txt'
                  placeholderColor='txt'
                  chevronColor='#191D3A'
                  height='43px'
                  textAlign='center'
                  cursor={typeOfTeacherClass === "both" ? "pointer" : "default"}
                />
              </div>
              
              <div className='students-control'>
                <SelectButton
                  options={getNumberOfStudentsOptions(teacherInfo, typeOfClass)}
                  value={numberOfStudents}
                  onChange={updateNumberOfStudents}
                  placeholder='Number of Students'
                  bgColor='white'
                  showIndicator={
                    getNumberOfStudentsOptions(teacherInfo, typeOfClass)
                      ?.length != 1
                  }
                  isDisabled={
                    getNumberOfStudentsOptions(teacherInfo, typeOfClass)
                      ?.length === 1
                  }
                  labelSize={"small"}
                  valueColor='txt'
                  placeholderColor='txt'
                  chevronColor='#191D3A'
                  height='43px'
                  textAlign='center'
                />
              </div>
            </FilterControlsWrapper>

            {isLoading ? (
              <LoaderComponent />
            ) : null}

            {availableTimings.length === 0 && !isLoading ? (
              <div className='no-classes-message'>
                No available classes found for the selected date and class type.
              </div>
            ) : (
              <ScheduleCardContainer>
                {availableTimings?.slice(0, 5).map((timing) => (
                  <DateCard
                    key={timing.id}
                    date={{
                      ...timing,
                      // Hide TYPE column in the right schedule card.
                      type: null,
                    }}
                    action={
                      <ActionContainer>
                        <ChooseButton
                          onClick={() => {
                            token
                              ? handleSelectDate(timing)
                              : setShowSignupModal(true)
                          }}>
                          Choose
                        </ChooseButton>

                        {renderAvailabilityInfo(timing)}
                      </ActionContainer>
                    }
                  />
                ))}
              </ScheduleCardContainer>
            )}

            <ShowMoreDatesButton
              onClick={() =>
                token
                  ? navigate(userRoute.showMoreDates + classDetail?.id, {
                      state: {
                        typeOfClass: typeOfClass,
                      },
                    })
                  : setShowSignupModal(true)
              }>
              Show more dates
            </ShowMoreDatesButton>
          </>
        )}
      </PricingDetailsCard>
      {/* ✅ Mobile Floating Button */}
      <FloatingBookButton
        variant='secondary'
        onClick={() =>
          token
            ? navigate(userRoute.showMoreDates + classDetail?.id, {
                state: { typeOfClass: typeOfClass },
              })
            : setShowSignupModal(true)
        }>
        Book Class
      </FloatingBookButton>
    </ClassInfoLayout>
  )
}

const ClassInfoLayout = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: Montserrat;

  @media (width >= ${mediaSizes.lg}) {
    display: contents;
  }
`

const ClassDetailsSection = styled.div`
  width: 100%;
  display: grid;
  gap: 1rem;
  padding-block: 0.5rem;
  height: fit-content;
  @media (width >= ${mediaSizes.md}) {
    padding-block: 1rem;
  }

  @media (width >= ${mediaSizes.lg}) {
    padding-block: 2rem;
    gap: 1.5rem;
  }
`

const SectionHeading = styled.p`
  font-family: Poppins, sans-serif;
  color: #1d1d1f;
  font-size: ${fonts.sizes.big};
  font-weight: 700;
`

// used for ref for CaptionSpot
const ActionContainer = styled.div``

const CaptionSpot = styled.p`
  color: ${fonts.sizes.blue};
  font-size: ${fonts.sizes.small};
  font-weight: 600;
  margin-top: 4px;
  height: 0;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ActionContainer}:hover & {
    opacity: 1;
    height: auto;
  }
`

const ContentSection = styled.div`
  display: grid;
  gap: 0.5rem;
  overflow-wrap: anywhere;

  & > .text {
    font-size: 14px;
    color: rgba(29, 29, 31, 0.75);
    line-height: 1.7;
  }

  @media (width >= ${mediaSizes.md}) {
    gap: 1rem;
  }

`

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  row-gap: 0.3rem;
  column-gap: 2rem;
  flex-wrap: wrap;
`

const IconTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${defaultThemeColors.txt};

  & > p {
    font-size: 13px;
    color: rgba(29, 29, 31, 0.75);
  }
`

const ProfileInfoWrapper = styled.div`
  display: flex;
  gap: 13px;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  & .profile-group {
    display: flex;
    gap: 13px;
    justify-content: flex-start;
    align-items: center;
  }

  & .toggle-group {
    margin-right: 10%;
  }
`

const AvatarWrapper = styled.div`
  height: 80px;
  width: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${defaultThemeColors.buttonBgNeutral};
  border-radius: 99px;

  & > svg {
    height: 40px;
    width: 40px;
  }

  & > img {
    height: 100%;
    width: 100%;
    aspect-ratio: 1/1;
    object-fit: cover;
    border-radius: 99px;
  }
`

const ProfileInfo = styled.div`
  display: grid;
  gap: 5px;
`

const RatingText = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: #1d1d1f;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  & > .rating {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    color: ${defaultThemeColors.pink};

    & > svg {
      width: 0.85rem;
      height: 0.85rem;
    }
  }

  & > .review {
    color: #1d1d1f;
    opacity: 0.6;
  }
`

const PricingDetails = styled.p`
  font-weight: 600;
  color: rgba(29, 29, 31, 0.6);
  font-size: 13px;

  & > :nth-child(1) {
    color: #1d1d1f;
  }
`

const PricingDetailsCard = styled.div`
  position: sticky;
  top: 2rem;
  width: 100%;
  box-sizing: border-box;
  display: grid;
  gap: 1.5rem;
  background-color: #f7f5f1;
  border: 1px solid rgba(29, 29, 31, 0.08);
  border-radius: 10px;
  height: fit-content;
  padding: 0.8rem;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100dvh - 2.8rem);
  ${scrollbarStylesThin}

  @media (max-width: 1023px) {
    display: none;
  }

  @media (min-width: 768px) {
    padding: 28px 24px 24px;
  }

  @media (width >= ${mediaSizes.lg}) {
    grid-column: 2;
    grid-row: 1 / span 4  ;
    align-self: start;
    margin-left: 0;
    margin-top: calc(${fonts.sizes["h3.4"]} + 1.6rem);
  }
`

const PricingHeaderWrapper = styled.div`
  display: grid;
  gap: 0.5rem;
  height: fit-content;

  & ${SectionHeading} {
    font-size: 24px;
    line-height: 1.2;
  }
`

const PricingSubHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: fit-content;
`

const FilterControlsWrapper = styled.div`
  display: grid;
  gap: 10px;
  height: fit-content;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  & > :first-child {
    grid-column: 1 / -1;
  }

  .auto-complete__single-value,
  .auto-complete__placeholder {
    font-size: 14px;
    font-weight: 500;
  }
`

const ScheduleCardContainer = styled.div`
  display: grid;
  gap: 0;

  & > div {
    border-bottom-color: rgba(29, 29, 31, 0.1);
    border-bottom-width: 1px;
    padding-block: 1rem;
  }

  & > div:first-child {
    padding-top: 0;
  }
`

const HeaderCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`

const ChooseButton = styled(ActionButton)`
  border-radius: 100px;
  border: 1.5px solid rgba(29, 29, 31, 0.35);
  color: #1d1d1f;
  background: transparent;
  padding: 8px 18px;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 500;
  min-width: unset;

  &:hover {
    border-color: #1d1d1f;
    background: rgba(29, 29, 31, 0.04);
    opacity: 1;
  }
`

const ShowMoreDatesButton = styled(ButtonPrimary)`
  background: #1d1d1f;
  border-radius: 100px;
  color: #ffffff;
  font-family: Poppins, sans-serif;
  font-size: 15px;
  font-weight: 500;
  padding: 16px;
  border: none;

  &:hover {
    background: #2d2d2f;
    opacity: 1;
  }
`

const FloatingBookButton = styled(ButtonPrimary)`
  display: none;
`
